import {Injectable} from '@angular/core';
import {OfflinePeerConnectionApiService} from '../../../../services/api/network/offline/peer-connection/offline-peer-connection-api.service';
import {OfflineVoipPeerApiService} from '../../../../services/api/network/offline/voip-peer/offline-voip-peer-api.service';
import {OfflineNodeApiService} from '../../../../services/api/network/offline/node/offline-node-api.service';
import {OfflineRoutingInterfaceApiService} from '../../../../services/api/network/offline/routing-interface/offline-routing-interface-api.service';
import {OfflineConnectionApiService} from '../../../../services/api/network/offline/connection/offline-connection-api.service';
import {OfflineNetworkApiService} from '../../../../services/api/network/offline/network/offline-network-api.service';
import {QualityBasedRoutingApiService} from '../../../../services/api/settings/routing/quality-based-routing/quality-based-routing-api.service';
import {RoutingSettingsApiService} from '../../../../services/api/settings/routing/routing-settings/routing-settings-api.service';
import {AttributeManipulationGroupsApiService} from '../../../../services/api/settings/call-flow-configurations/attribute-manipulation-groups/attribute-manipulation-groups-api.service';
import {RoutingServersSettingsApiService} from '../../../../services/api/routing-servers/routing-servers-settings-api.service';
import * as _ from 'lodash';
import {MatDialog} from '@angular/material/dialog';
import {OfflinePconsDialogComponent} from '../../../offline-map/dialogs/offline-pcons-dialog/offline-pcons-dialog.component';
import Search from '../../../../utils/search';
import {ConfirmationDialogComponent} from '../../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {InformationDialogComponent} from '../../../../dialogs/information-dialog/information-dialog.component';
import {TestRouteDialogComponent} from '../../../map/test-route-dialog/test-route-dialog.component';
import {AcDialogService} from 'ac-infra';
import {SnackBarService} from '../../../../services/common/snack-bar/snack-bar.service';
import {OfflineMapService} from '../../map/offline-map/offline-map.service';
import {VirtualPconsDialogComponent} from '../../../pcons/virtual-pcons-dialog/virtual-pcons-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class OfflinePeerConnectionService {

  constructor(private dialog: MatDialog,
              private offlinePconApiService: OfflinePeerConnectionApiService,
              private offlineVoipPeerApiService: OfflineVoipPeerApiService,
              private offlineNodeApiService: OfflineNodeApiService,
              private offlineRoutingInterfaceApiService: OfflineRoutingInterfaceApiService,
              private offlineConnectionApiService: OfflineConnectionApiService,
              private offlineNetworkApiService: OfflineNetworkApiService,
              private qualityBasedRoutingApiService: QualityBasedRoutingApiService,
              private attributeManipulationGroupsApiService: AttributeManipulationGroupsApiService,
              private routingSettingsApiService: RoutingSettingsApiService,
              private routingServerApiService: RoutingServersSettingsApiService,
              private offlineMapService: OfflineMapService,
              private snackBarService: SnackBarService,
              private acDialogService: AcDialogService) {
  }

  public lockPcon(id: number): void {
    this.offlinePconApiService.lockPCon(id).then(() => {
      this.refreshDiagram();
    }).catch(() => {});
  }

  public unlockPcon(id: number): void {
    this.offlinePconApiService.unlockPCon(id).then(() => {
      this.refreshDiagram();
    }).catch(() => {});
  }

  public lockPcons(ids): void {
    this.offlinePconApiService.lockPCons(ids).then(() => {
      this.refreshDiagram();
    }).catch(() => {});
  }

  public unlockPcons(ids): void {
    this.offlinePconApiService.unlockPCons(ids).then(() => {
      this.refreshDiagram();
    }).catch(() => {});
  }

  public lockOutgoingPCons(ids): void {
    this.offlinePconApiService.lockOutgoingPCons(ids).then(() => {
      this.refreshDiagram();
    }).catch(() => {});
  }

  public lockIncomingPCons(ids): void {
    this.offlinePconApiService.lockIncomingPCons(ids).then(() => {
      this.refreshDiagram();
    }).catch(() => {});
  }

  public editVirtualPCon(id: number): void {
    Promise.all([this.offlinePconApiService.getPconForMap(id),
      this.offlineVoipPeerApiService.getVoipPeersForMap()
    ]).then(data => {
      const selectedVirtualPCon = data[0];
      const voipPeersOptions = Search.generateSearchOptions(data[1], 'name', 'id');

      const dialogRef = this.dialog.open(VirtualPconsDialogComponent, {
        data: {
          isEditMode: true,
          selectedItem: _.cloneDeep(selectedVirtualPCon),
          voipPeers: voipPeersOptions
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.offlinePconApiService.editPcon(result.selectedItem, selectedVirtualPCon.id).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.selectedItem.name}" successfully edited`);
          this.refreshDiagram();
        });
      });
    }).catch(() => {});
  }

  public addVirtualPCon(fromNode: any, toVoipPeer: any): void {
    Promise.all([this.offlineVoipPeerApiService.getVoipPeersForMap(), this.offlineNodeApiService.getNodeForMap(fromNode)]).then(value => {

      const pCon = {
        nodeId: fromNode,
        nodeName: value[1].name,
        voipPeerId: toVoipPeer ? toVoipPeer : null,
        weight: 50,
        peerConnectionNodeType: 'Virtual'
      };

      const voipPeersOptions = Search.generateSearchOptions(value[0], 'name', 'id');

      const dialogRef = this.dialog.open(VirtualPconsDialogComponent, {
        data: {
          isEditMode: false,
          selectedItem: pCon,
          voipPeers: voipPeersOptions
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.offlinePconApiService.addPcon(result.selectedItem).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.selectedItem.name}" successfully added`);
          this.refreshDiagram();
        });
      });

      dialogRef.componentInstance.onCancel.subscribe(() => this.refreshDiagram());
    }, () => {
      this.refreshDiagram();
    });
  }

  public addPcon(fromNodeId ?: number, toVoipPeerId ?: number): void {
    Promise.all([
      this.offlineVoipPeerApiService.getVoipPeersForMap(),
      this.offlineNodeApiService.getNodeForMap(fromNodeId),
      this.offlineRoutingInterfaceApiService.getRoutingInterfacesForMap({filter: {nodeId: fromNodeId}}),
      this.attributeManipulationGroupsApiService.getAttributeManipulationGroups().toPromise(),
      this.qualityBasedRoutingApiService.getQualityBasedRoutingSettings().toPromise()
    ]).then(value => {

      const pCon = {
        nodeId: fromNodeId,
        nodeName: value[1].name,
        voipPeerId: toVoipPeerId,
        weight: 50,
        peerConnectionNodeType: 'IPGroup'
      };

      const dialogRef = this.dialog.open(OfflinePconsDialogComponent, {
        data: {
          isEditMode: false,
          selectedItem: _.cloneDeep(pCon),
          voipPeers: Search.generateSearchOptions(value[0], 'name', 'id'),
          rIs: value[2],
          node: value[1],
          attrManipulation: Search.generateSearchOptions(value[3], 'groupName', 'id'),
          advancedConditions: value[4]
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.offlinePconApiService.addPcon(result.selectedItem).then(() => {
          dialogRef.close();
          this.refreshDiagram();
        }).catch(() => {});
      });

      dialogRef.componentInstance.onCancel.subscribe(() => this.refreshDiagram());
    });
  }

  public editPcon(id: number): void {
    Promise.all([this.offlinePconApiService.getPconForMap(id),
      this.offlineVoipPeerApiService.getVoipPeersForMap(),
      this.attributeManipulationGroupsApiService.getAttributeManipulationGroups().toPromise(),
      this.qualityBasedRoutingApiService.getQualityBasedRoutingSettings().toPromise(),
      this.offlineRoutingInterfaceApiService.getRoutingInterfacesForMap(),
    ]).then(value => {
      const dialogRef = this.dialog.open(OfflinePconsDialogComponent, {
        data: {
          isEditMode: true,
          selectedItem: _.cloneDeep(value[0]),
          voipPeers: Search.generateSearchOptions(value[1], 'name', 'id'),
          attrManipulation: Search.generateSearchOptions(value[2], 'groupName', 'id'),
          advancedConditions: value[3],
          rIs: value[4]
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.offlinePconApiService.editPcon(result.selectedItem, id).then(() => {
          dialogRef.close();
          this.refreshDiagram();
        }).catch(() => {});
      });
    });
  }

  public deletePcon(pCon): void {
    const confirmationMessage = `Are you sure you want to delete the peer connection: "${pCon.name}"?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      this.offlinePconApiService.deletePCon(pCon.id).then(() => {
        dialogRef.close();
        this.snackBarService.display(`"${pCon.name}" successfully deleted`);
        this.refreshDiagram();
      });
    });
  }

  public testRoute(pConId: number, testRouteRequest?): void {
    const isRepeatRequest = !!testRouteRequest;
    const testRouteObject = {};

    Promise.all([this.routingSettingsApiService.getRoutingSettings().toPromise(),
      this.offlinePconApiService.getPConsForMap(),
      this.offlineNodeApiService.getNodesForMap(),
      this.offlineConnectionApiService.getConnectionsForMap(),
      this.routingServerApiService.getRoutingServers().toPromise()]).then((value) => {
      const dataObject: any = {
        testRouteParameters: value[0],
        pCons: value[1],
        nodes: value[2],
        connections: value[3]
      };

      dataObject.routers = value[4].filter(router => {
        return router.adminState === 'UNLOCKED' && router.status === 'AVAILABLE';
      });

      const selectedPCon = dataObject.pCons.find((pCon) => {
        return pCon.id === pConId;
      });

      if (isRepeatRequest && !selectedPCon) {
        const dialogRef = this.dialog.open(InformationDialogComponent, {
          width: '460px',
          data: {
            message: 'Peer Connection does not exist'
          }
        });

        dialogRef.componentInstance.onApply.subscribe(() => {
          dialogRef.close();
        });
        return;
      }

      if (selectedPCon && (selectedPCon.adminState === 'LOCKED' || selectedPCon.adminState === 'LOCKED_INCOMING')) {
        const message = `Peer Connection ${selectedPCon.name} is locked ${(selectedPCon.adminState === 'LOCKED_INCOMING' ? ' for incoming' :
          '')} . \nDo you want to continue with test route ?`;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          width: '460px',
          data: { message }
        });

        dialogRef.componentInstance.onApply.subscribe(() => {
          dialogRef.close();
          this.testRouteInner(pConId, testRouteObject, dataObject, isRepeatRequest);
        });
      } else {
        this.testRouteInner(pConId, testRouteObject, dataObject, isRepeatRequest);
      }
    });
  }

  private refreshDiagram(): void {
    this.offlineMapService.refresh();
  }

  private editVirtualPconInServer(pCon, id: number): void {
    this.offlinePconApiService.editPcon(pCon, id).then(() => {
      this.refreshDiagram();
      // this.sharedEventService.broadcast('closeVirtualPeerConnectionDialog');
    }).catch(() => {});
  }

  private addVirtualPconInServer(pCon): void {
    this.offlinePconApiService.addPcon(pCon).then(() => {
      // this.sharedEventService.broadcast('closeOfflineVirtualPeerConnectionDialog');
      this.refreshDiagram();
    }).catch(() => {});
  }

  private editOfflinePconInServer(pCon, id: number): void {
    this.offlinePconApiService.editPcon(pCon, id).then(() => {
      this.refreshDiagram();
      // this.sharedEventService.broadcast('closeOfflinePeerConnectionDialog');
    }).catch(() => {});
  }

  private addPconInServer(pCon): void {
    this.offlinePconApiService.addPcon(pCon).then(() => {
      // this.sharedEventService.broadcast('closeOfflinePeerConnectionDialog');
      this.refreshDiagram();
    }).catch(() => {});
  }

  private testRouteInner(pConId: number, testRouteObject, dataObject, repeat): void {
    testRouteObject.maxRoutes = dataObject.testRouteParameters.maxRoutes;
    testRouteObject.maxRoutesPerDestination = dataObject.testRouteParameters.maxRoutesPerAction;
    delete dataObject.testRouteParameters;

    const dialogData: any = {
      testRouteObject,
      selectedPConId: pConId,
      dataObject,
      repeat,
      isOffline: true
    };

    this.acDialogService.open(TestRouteDialogComponent, {
      dialogData,
      onSubmit: () => {
        this.offlineNetworkApiService.testRoute(testRouteObject).then((testRouteResult) => {
          this.offlineMapService.testRoute({testRouteObject, testRouteResult});
        }).catch(() => {});
      }
    });
  }
}
